import { useEffect } from "react";
import DICT from "./translations.json";

const TEXT = new WeakMap();
const ATTR = new WeakMap();
const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = '[translate="no"],.notranslate,script,style,code,textarea,[class*="cl-"]';

const exact = (s, lang) => DICT[s]?.[lang];
const translateKey = (key, lang) => {
  const hit = exact(key, lang);
  if (hit) return hit;
  let m = key.match(/^(\d[\d,.]*)\s+(.+)$/);
  if (m && exact(m[2], lang)) return `${m[1]} ${exact(m[2], lang)}`;
  m = key.match(/^(.+?)\s*\((\d+)\)$/);
  if (m && exact(m[1], lang)) return `${exact(m[1], lang)} (${m[2]})`;
  if (/ · |: /.test(key)) {
    const parts = key.split(/( · |: )/);
    const out = parts.map((p) => (p === " · " || p === ": " ? p : exact(p.trim(), lang) || exact(p.trim().replace(/_/g, " "), lang) || p));
    if (out.some((p, i) => p !== parts[i])) return out.join("");
  }
  return exact(key.replace(/_/g, " "), lang) || null;
};

const lookup = (en, lang) => {
  if (lang === "en") return en;
  const key = en.trim().replace(/\s+/g, " ");
  const tr = translateKey(key, lang);
  return tr ? en.replace(en.trim(), tr) : en;
};

function doText(node, lang) {
  const cur = node.nodeValue;
  if (!cur || !cur.trim()) return;
  const p = node.parentElement;
  if (!p || p.closest(SKIP)) return;
  let o = TEXT.get(node);
  if (!o || (cur !== o.en && cur !== o.tr)) o = { en: cur };
  o.tr = lookup(o.en, lang);
  TEXT.set(node, o);
  if (cur !== o.tr) node.nodeValue = o.tr;
}

function doAttrs(el, lang) {
  if (el.closest(SKIP)) return;
  const m = ATTR.get(el) || {};
  ATTRS.forEach((a) => {
    const cur = el.getAttribute(a);
    if (!cur) return;
    let o = m[a];
    if (!o || (cur !== o.en && cur !== o.tr)) o = { en: cur };
    o.tr = lookup(o.en, lang);
    m[a] = o;
    if (cur !== o.tr) el.setAttribute(a, o.tr);
  });
  ATTR.set(el, m);
}

function walk(root, lang) {
  if (root.nodeType === 3) return doText(root, lang);
  if (root.nodeType !== 1) return;
  if (root.matches?.(ATTRS.map((a) => `[${a}]`).join(","))) doAttrs(root, lang);
  root.querySelectorAll?.(ATTRS.map((a) => `[${a}]`).join(",")).forEach((el) => doAttrs(el, lang));
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) doText(n, lang);
}

export const translateText = (s, lang) => translateKey(s.trim().replace(/\s+/g, " "), lang);

export function applyBilingual(root) {
  if (!root) return;
  root.querySelectorAll("[data-doc-lang]").forEach((page) => {
    const lang = page.getAttribute("data-doc-lang");
    if (!lang || lang === "en") return;
    const w = document.createTreeWalker(page, NodeFilter.SHOW_TEXT);
    for (let n = w.nextNode(); n; n = w.nextNode()) {
      const cur = n.nodeValue;
      const o = TEXT.get(n);
      if ((o && o.bi === cur) || n.parentElement?.closest("[data-no-bi]")) continue;
      const key = cur.trim().replace(/:$/, "");
      if (!key || key.length < 2) continue;
      const tr = translateText(key, lang) || translateText(`${key}:`, lang)?.replace(/:$/, "");
      if (!tr || tr === key) continue;
      const bi = cur.replace(key, key.includes(" / ") ? `${key} (${tr})` : `${key} / ${tr}`);
      n.nodeValue = bi;
      TEXT.set(n, { en: cur, tr: bi, bi });
    }
  });
}

export function useDomTranslator(lang) {
  useEffect(() => {
    document.documentElement.lang = lang;
    walk(document.body, lang);
    const obs = new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === "characterData") doText(m.target, lang);
        else if (m.type === "attributes") doAttrs(m.target, lang);
        else m.addedNodes.forEach((n) => walk(n, lang));
      });
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    return () => obs.disconnect();
  }, [lang]);
}
